import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { Plus } from '../../../../assets';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { GetItemsByIdsRequest, Item } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../../hooks/useLogError';
import { StockCountCreateContext, StockCountCreateValues } from '../../contexts/StockCountCreateContext';
import CloseIcon from '@mui/icons-material/Close';

interface NotAddedItem {
  itemNumber: string;
  reason: string;
}

interface GetItemsByIdsResponse {
  data?: {
    items?: Item[];
    notAddedItems?: NotAddedItem[];
  };
}

const StockCountCreateBulkModal = () => {
  const { isBulkModalOpen,
    handleClose,
    handleBulkItems } = useContext(StockCountCreateContext) as StockCountCreateValues;

  const [inputString, setInputString] = useState('');
  const { selectedStore } = useContext(UserContext);
  const { getItemsById } = useNswagClient();
  const [bulkAddSuccess, setBulkAddSuccess] = useState<Item[]>([]);
  const [bulkAddFailed, setBulkAddFailed] = useState<NotAddedItem[]>([]);
  const [changeContext, setChangeContext] = useState<boolean>(false);
  const { t } = useTranslation('createStockCount');
  const { logError } = useLogError();

  const handleConfirm = (data: Item[], failedData: NotAddedItem[]) => {
    if (failedData.length === 0) {
      handleBulkItems(data);
      closeModal();
    } else {
      handleBulkItems(data);
      setChangeContext(true);
    }
  };

  const closeModal = () => {
    setChangeContext(false);
    setBulkAddFailed([]);
    setBulkAddSuccess([]);
    handleClose();
  };

  const getData = () => {
    let array = inputString.split(' ').map((item) => item.trim());
    array = array.flatMap((item) => item.split('\n').map((innerItem) => innerItem.trim()));
    array = array.filter((item) => item.length > 0);
    const body: GetItemsByIdsRequest = {
      storeNumber: selectedStore?.storeNumber,
      itemNumbers: array,
      stockItemsOnly: true,
    };

    getItemsById(body).then((result) => {
      processGetResponse(result as unknown as GetItemsByIdsResponse, body);
    })
      .catch((error) => {
        logError(error);
      });
  };

  const processGetResponse = (result: GetItemsByIdsResponse, body: GetItemsByIdsRequest) => {
    if (result?.data) {
      const items = result.data.items || [];
      const notAddedItems = result.data.notAddedItems || [];

      const newItemsList = [...bulkAddSuccess];
      for (const item of items) {
        if (!newItemsList.some(x => x.itemNumber === item.itemNumber)) {
          newItemsList.push(item);
        }
      }
      setBulkAddSuccess(newItemsList);

      const failedList = [...bulkAddFailed];
      for (const notAdded of notAddedItems) {
        if (!failedList.some(x => x.itemNumber === notAdded.itemNumber)) {
          failedList.push(notAdded);
        }
      }

      // Also add any item numbers that are completely missing from both lists
      const allReturnedNumbers = [
        ...items.map(i => i.itemNumber),
        ...notAddedItems.map(i => i.itemNumber),
      ];
      const missingItemNumbers = body.itemNumbers?.filter(x => !allReturnedNumbers.includes(x)) || [];
      for (const missing of missingItemNumbers) {
        if (!failedList.some(x => x.itemNumber === missing)) {
          failedList.push({ itemNumber: missing, reason: t('itemNotFound') });
        }
      }

      setBulkAddFailed(failedList);
      handleConfirm(newItemsList, failedList);
    } else {
      logError(result);
    }
  };

  return (
    <>
      {!changeContext ? (
        <Dialog open={isBulkModalOpen}
          onClose={closeModal}
          maxWidth='md'
          fullWidth>
          <DialogTitle>
            {t('addBulk')}
            <IconButton
              onClick={closeModal}
              sx={{
                position: 'absolute',
                right: 16,
                top: 8,
                color: (theme) => theme.palette.custom.gray[800],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Alert severity='info'>
              {t('info')}
            </Alert>
            <Box sx={{ pt: '10px' }}>
              <TextField
                placeholder='Add text'
                multiline
                rows={10}
                onChange={(e) => setInputString(e.target.value)}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions >
            <Button
              variant="secondary"
              onClick={closeModal}>
              {t('cancel')}
            </Button>
            <Button onClick={getData}
              startIcon={<Plus />}>
              {t('add')}
            </Button>
          </DialogActions>
        </Dialog>
      )
        :
        (
          <Dialog open={isBulkModalOpen}
            onClose={closeModal}
            maxWidth='md'
            fullWidth>
            <DialogTitle>
              {t('failedSkusTitle')}
              <IconButton
                onClick={closeModal}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 8,
                  color: (theme) => theme.palette.custom.gray[800],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Alert severity='warning'
                sx={{ mb: 2 }}>
                {t('failedSkus')}
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('itemNumber')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('reason')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkAddFailed.map((item, index) => (
                      <TableRow key={item.itemNumber}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.itemNumber}</TableCell>
                        <TableCell>{item.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions >
              <Button variant="secondary"
                size='lg'
                onClick={closeModal}>
                {t('cancel')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
    </>
  );
};

export default StockCountCreateBulkModal;